/**
 * FUNNEL STATUS GUARD - ROUTING LOGIC FLOWCHART
 * ==============================================
 * 
 * This guard controls access to all funnel pages based on driver status, authentication, and various flags.
 * 
 * HIGH-LEVEL FLOW:
 * ----------------
 * 
 * 1. AUTHENTICATION CHECK
 *    ├─ No token? → Logout & redirect to login
 *    ├─ Admin user? → Redirect to dashboard
 *    ├─ No path or empty path or 2FA without user? → Redirect to login
 *    ├─ 2FA with user in localStorage? → Allow access
 *    ├─ Public pages (login/signup/password) without token? → Allow access
 *    ├─ Login/signup pages with token? → Redirect based on status
 *    ├─ Status undefined or missing? → Logout & redirect to login
 *    └─ No token after checks? → Logout & redirect to login
 * 
 * 2. USER DATA FETCH
 *    ├─ User summary cached? → Use cached data & proceed to status update
 *    ├─ Ongoing fetch exists? → Return existing observable
 *    └─ No cache? → Fetch from API (with ongoing request deduplication)
 *        └─ On API error → Logout & deny access
 * 
 * 3. SPECIFIC ACCESS CHECKS (Priority Order in statusupdate)
 *    ├─ Reapply page → Check former_driver status & activity level
 *    ├─ Uber Partner referral → Validate Journey TV AND Uber Connect (either required)
 *    ├─ Mail in process → Allow payment page access only
 *    ├─ Funnel pages → Progressive access based on completion status
 *    │   ├─ TOS_COMPLETE → info only
 *    │   ├─ DRIVER_INFO_COMPLETE → info, profile
 *    │   ├─ PROFILE_COMPLETE → info, profile, payment
 *    │   ├─ PAYMENT_COMPLETE → info, profile, payment, [uber-connect-required if flag ON] OR [proof_of_rides if flag OFF]
 *    │   └─ Reapply activity level → Cannot access funnel pages
 *    ├─ Complete page → Requires payment_complete + (texted_logs="yes" with rides>0 OR texted_logs starting with "uber_pass" or "uber_fail" OR reapply activity level with allowUberConnection=false OR allowUberConnection=false with texted_logs="no" or empty)
 *    │   └─ Reapply activity level with allowUberConnection=true → Denied access to complete page
 *    ├─ Uber Connect Required → payment_complete + (allowUberConnection=true OR texted_logs="uber_required_manual_logs_failed")
 *    │   └─ Invalid access handling: allowUberConnection=false redirects based on texted_logs or activity level:
 *    │       ├─ "uber_pass" OR "uber_fail" OR "yes" OR reapply activity level → complete page
 *    │       └─ other → incomplete-and-contact-support
 *    ├─ Incomplete & Contact Support → payment_complete + allowUberConnection=false + texted_logs not "yes" AND not starts with "uber_"
 *    └─ Uber Link page → Only accessible for ACTIVE_DRIVER, FORMER_DRIVER, FORMER_DRIVER_MAIL_DELIVERED, or MAIL_DELIVERED statuses
 * 
 * 4. REDIRECT LOGIC
 *    └─ Should redirect to complete? → If payment_complete + (yes+rides OR uber_pass) AND not already on complete page
 * 
 * 5. GENERAL RESTRICTIONS
 *    ├─ Login/signup/reset/set-password routes → Redirect to status-based route
 *    ├─ Route-specific status requirements (proof_of_rides, complete, payment, profile, info, uber-connect-required, uber-link, etc.)
 *    ├─ Status-based restrictions (do_not_contact, ready_for_tablet, mail_in_process, mailing_issue, not_interested, waitlist variants)
 *    └─ Redirect to status-based default route if restricted
 * 
 * 6. FALLBACK
 *    └─ Redirect based on payment_complete state and uber connection flags:
 *        ├─ Not payment_complete → home (/)
 *        ├─ allowUberConnection=true → uber-connect-required
 *        ├─ texted_logs starts with "uber_" → complete
 *        └─ other → incomplete-and-contact-support
 * 
 * KEY ENVIRONMENT FLAGS:
 * ----------------------
 * - ENFORCE_UBER_CONNECT_FLAG: Controls default route for payment_complete status and allowed paths
 *   └─ TRUE: payment_complete routes to uber-connect-required; allowed paths include uber-connect-required (NOT proof_of_rides)
 *   └─ FALSE: payment_complete routes to proof_of_rides; allowed paths include proof_of_rides
 * 
 * IMPORTANT STATUSES:
 * -------------------
 * - tos_complete → info
 * - driver_info_complete → info, profile
 * - profile_complete → info, profile, payment
 * - payment_complete → [uber-connect-required (flag ON)] OR [proof_of_rides (flag OFF)]
 * - do_not_contact, ready_for_tablet, mail_in_process, mailing_issue, not_interested → specific restricted pages
 * - interested_dnq_rides → waitlist
 * - interested_dnq_market, interested_dnq_other → waitlist_other
 * - waitlisted → waitlist_demand
 * - former_driver, former_driver_mail_delivered → may access reapply based on activity_level & tablet conditions
 * - active_driver, former_driver, former_driver_mail_delivered, mail_delivered → may access uber-link page
 * - complete page: payment_complete + (texted_logs="yes" with rides>0 OR texted_logs starting with "uber_pass" or "uber_fail")
 * 
 * UBER CONNECT FLOW (when ENFORCE_UBER_CONNECT_FLAG = true):
 * -----------------------------------------------------------
 * payment_complete users follow this flow:
 * 
 *     allowUberConnection?
 *          │
 *          ├─ YES → uber-connect-required page
 *          │         │
 *          │         └─ After connecting → texted_logs updated
 *          │
 *          └─ NO → Check texted_logs
 *                   │
 *                   ├─ "uber_pass" OR "yes" (with rides) → complete page
 *                   ├─ starts with "uber_pass" or "uber_fail" → complete page
 *                   └─ other → incomplete-and-contact-support
 * 
 * UBER PARTNER REFERRAL:
 * ----------------------
 * Users referred by Uber Partners (referral_info.referred_by starts with "Uber_Partner") must have:
 * - Journey TV matched identifier (journey_tv.matched_identifier.length > 0) OR
 * - Uber Connect status = 'CONNECTED'
 * If NEITHER condition is met → Redirect to UBER_AUTHORIZE route with query param error='uber_partner_referral_not_connected'
 * 
 * CONSTRUCTOR INITIALIZATION:
 * ---------------------------
 * Updates statusRoute['payment_complete'] based on ENFORCE_UBER_CONNECT_FLAG at guard initialization
 */

import { Injectable } from '@angular/core';
import {
    CanActivate,
    ActivatedRouteSnapshot,
    UrlTree,
    Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { JoinService } from '../../services/join/join.service';
import { ApiDriverInterfaceService } from '../../services/api-interface/api-driver-interface';
import { FunnelGuardSyncService } from '../../services/driver/funnel-guard-sync.service';
import { environment } from '../../../../environments/environment';
import {
    DriverStatusEnum,
    UBER_REAUTH_DASHBOARD_STATUSES,
    UBER_REAUTH_EXCLUDED_STATUSES,
} from '../../../shared/constants/driver-status.constant';
import { ActivityLevelsEnum } from '../../../shared/constants/activity-level.constant';
import { TextedLogsEnum } from '../../../shared/constants/texted-logs.constant';
import { RoutePaths, statusRoute } from '../../../shared/constants/route-paths.constant';

/**
 * Interface for user summary data structure.
 * Used to type the user summary object fetched from the API.
 */
interface UserSummary {
    texted_logs?: string;
    status?: string;
    activity_level?: string;
    driver_info?: {
        lifetime_rides?: number | null;
    };
    tablet?: string;
    referral_info?: {
        referred_by?: string;
    };
    journey_tv?: {
        matched_identifier?: string[] | null;
    };
    uber_connect?: {
        status?: string;
    };
    allow_uber_connection?: boolean;
}

/**
 * Interface for user information extracted from UserSummary or local storage.
 */
interface UserInfo {
    textedLogs: string;
    driverStatus: string;
    activityLevel: string;
    lifetimeRides: number;
    tabletId: string;
    allowUberConnection: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class FunnelStatusGuard implements CanActivate {

    private ongoingFetch$: Observable<boolean> | null = null;
    private reauthCache: { token: string | null; needsReauth: boolean } | null = null;

    constructor(
        private readonly authService: AuthenticationService,
        private readonly router: Router,
        private readonly joinService: JoinService,
        private readonly apiDriverInterfaceService: ApiDriverInterfaceService,
        private readonly funnelGuardSyncService: FunnelGuardSyncService
    ) {

        // Update statusRoute's 'payment_complete' path based on environment flag
        statusRoute['payment_complete'] = environment.ENFORCE_UBER_CONNECT_FLAG ?
            RoutePaths.UBER_CONNECT_REQUIRED :
            RoutePaths.PROOF_OF_RIDES;
    }

    canActivate(
        route: ActivatedRouteSnapshot,
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        return this.checkLoginSession(route);
    }

    /**
     * Function to get information from the user summary or local storage
     * 
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {UserInfo} User Info object.
     */
    private getUserInfo(userSummary: UserSummary): UserInfo {
        const lifetimeRidesFromStorage = Number(localStorage.getItem('lifetime_rides'));
        return {
            textedLogs: userSummary?.texted_logs ?? localStorage.getItem('texted_logs_value') ?? '',
            driverStatus: userSummary?.status ?? localStorage.getItem('status') ?? '',
            activityLevel: userSummary?.activity_level ?? localStorage.getItem('activity_level') ?? '',
            lifetimeRides: userSummary?.driver_info?.lifetime_rides ?? (Number.isNaN(lifetimeRidesFromStorage) ? 0 : lifetimeRidesFromStorage),
            tabletId: userSummary?.tablet ?? localStorage.getItem('tablet_id') ?? '',
            allowUberConnection: userSummary?.allow_uber_connection ?? (localStorage.getItem('allow_uber_connection') === 'true'),
        };
    }

    /**
     * Handle admin user redirection to dashboard.
     * 
     * @param {boolean} token Authentication token presence.
     * @returns {boolean} True if the user is redirected, false otherwise.
     */
    private handleAdminRedirection(
        token: boolean
    ): boolean {
        const isAdmin = localStorage.getItem('isAdmin');
        if (isAdmin === 'true' && token) {
            this.router.navigate([RoutePaths.DASHBOARD_SUMMARY]);
            return false;
        }
        return true;
    }

    /**
     * Check if the 2FA page is being accessed as part of the phone number update flow,
     * which has no user object in local storage but carries a session auth token.
     *
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @returns {boolean} True if this is a phone update session, false otherwise.
     */
    private isPhoneUpdateSession(route: ActivatedRouteSnapshot): boolean {
        return (
            route.queryParamMap.get('type') === 'phone_update' &&
            !!sessionStorage.getItem('driverAuthToken')
        );
    }

    /**
     * Handle no user redirection to login page.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @returns {boolean} True if the user is redirected, false otherwise.
     */
    private handleNoUserRedirection(
        route: ActivatedRouteSnapshot
    ): boolean {

        const currentPath = route.routeConfig?.path;

        if (
            !currentPath || currentPath === '' || (
                currentPath.includes(RoutePaths.TWO_FACTOR_CODE) &&
                !localStorage.getItem('user') &&
                !this.isPhoneUpdateSession(route)
            )
        ) {
            this.router.navigate([RoutePaths.LOGIN]);
            return false;
        }
        return true;
    }

    /**
     * Handle 2FA page access based on user object presence in local storage.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @returns {boolean} True if the user can access 2FA page, false otherwise.
     */
    private handle2FARedirection(
        route: ActivatedRouteSnapshot,
    ): boolean {

        const currentPath = route.routeConfig?.path;
        if (
            currentPath?.includes(RoutePaths.TWO_FACTOR_CODE) &&
            (localStorage.getItem('user') || this.isPhoneUpdateSession(route))
        ) {
            return true;
        }
        return false;
    }

    /**
     * Handle tokenless page access for login, signup, and password reset pages.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {boolean} driverToken Authentication token presence.
     * @returns {boolean} True if the user can access the page, false otherwise.
     */
    private handleTokenlessPageAccess(
        route: ActivatedRouteSnapshot,
        driverToken: boolean,
    ): boolean {
        const currentPath = route.routeConfig?.path;

        // If the pages are login, signup, or forgot_password, check if the token
        // is present. If token is not present, allow access to these pages.
        if (
            (currentPath?.includes(RoutePaths.JOIN) ||
                currentPath?.includes(RoutePaths.LOGIN) ||
                currentPath?.includes(RoutePaths.PASSWORD) ||
                currentPath?.includes(RoutePaths.RESET) ||
                currentPath?.includes(RoutePaths.SET_PASSWORD)) && !driverToken
        ) {
            this.funnelGuardSyncService.setFunnelGuardSync(true);
            return true;
        }

        return false;
    }

    /**
     * Handle with token login/signup page redirection.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {boolean} driverToken Authentication token presence.
     * @param {string} driverStatus Driver status.
     * @returns {boolean} True if the user can access the page, false otherwise.
     */
    private handleWithTokenLoginRedirection(
        route: ActivatedRouteSnapshot,
        driverToken: boolean,
        driverStatus: string,
    ): boolean {
        const currentPath = route.routeConfig?.path;

        // If the pages are login, signup, or forgot_password, check if the token
        // is present. If token is present, redirect based on status.
        if (
            (currentPath?.includes(RoutePaths.JOIN) ||
                currentPath?.includes(RoutePaths.LOGIN) ||
                currentPath?.includes(RoutePaths.PASSWORD) ||
                currentPath?.includes(RoutePaths.RESET) ||
                currentPath?.includes(RoutePaths.SET_PASSWORD)) && driverToken
        ) {
            this.router.navigate([statusRoute[driverStatus]]);
            return true;
        }
        return false;
    }

    /**
     * Handle unknown status redirection.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {string} driverStatus Driver status.
     * @returns {boolean} True if the user can access the page, false otherwise.
     */
    private handleUnknownStatusRedirection(
        driverStatus: string,
    ): boolean {

        // Check if status is undefined or not present
        if (!driverStatus || driverStatus === 'undefined') {
            this.authService.logOut();
            return false;
        }
        return true;
    }

    /**
     * Handle token not present redirection to login page.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {boolean} driverToken Authentication token presence.
     * @returns {boolean} True if the user is redirected, false otherwise.
     */
    private handleTokenNotPresentRedirection(
        route: ActivatedRouteSnapshot,
        driverToken: boolean,
    ): boolean {
        // If token is not present, redirect to login page
        if (!driverToken) {
            this.authService.logOut();
            return false;
        }
        return true;
    }

    /**
     * Check which page to redirect to based on the user's status.
     *
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @returns {boolean} True if the user is logged in, false otherwise.
     */
    checkLoginSession(route: ActivatedRouteSnapshot): boolean | Observable<boolean> {
        const status: string = localStorage.getItem('status') || '',
            token: boolean = !!this.authService.getToken();

        console.log('current route path:', route.routeConfig?.path);

        // If the user is admin, redirect to the driver dashboard
        if (!this.handleAdminRedirection(token)) return false;

        // Login page redirection.
        // If no specific path is provided or if the path is empty or if the path is two_factor_code and
        // there is no user object in local storage, redirect to login page.
        if (!this.handleNoUserRedirection(route)) return false;

        // If page is two_factor_code, check if the local storage has the user
        // object. If not, redirect to login page.
        if (this.handle2FARedirection(route)) return true;

        // Handle tokenless page access for login, signup, and password reset pages
        if (this.handleTokenlessPageAccess(route, token)) return true;

        // Handle with token login/signup page redirection
        if (this.handleWithTokenLoginRedirection(route, token, status)) return true;

        // Handle unknown status redirection
        if (!this.handleUnknownStatusRedirection(status)) return true;

        // Handle token not present redirection
        if (!this.handleTokenNotPresentRedirection(route, token)) return true;

        // If token is present, check status and redirect accordingly
        if (token) {

            const userSummary = this.joinService.userSummaryDetails;

            // Fetch user summary details if not already present
            if (userSummary) {
                this.funnelGuardSyncService.setFunnelGuardSync(true);
                return this.statusUpdateWithReauthCheck(route, userSummary);
            }

            // Return ongoing fetch if one exists
            if (this.ongoingFetch$) {
                return this.ongoingFetch$;
            }

            // Emit loading state to show loading spinner
            this.funnelGuardSyncService.setFunnelGuardSync(false);

            // Wait for the fetch to complete before proceeding
            this.ongoingFetch$ = new Observable<boolean>((observer) => {
                this.joinService.getJoinDetails().subscribe({
                    next: (data) => {
                        this.joinService.setUserData(data?.data);

                        this.resolveWithReauthCheck(route, data?.data).subscribe((result) => {
                            this.funnelGuardSyncService.setFunnelGuardSync(true);
                            this.ongoingFetch$ = null;
                            observer.next(result);
                            observer.complete();
                        });
                    },
                    error: (error) => {
                        console.log(error);
                        this.authService.logOut();
                        this.funnelGuardSyncService.setFunnelGuardSync(true);
                        this.ongoingFetch$ = null;
                        observer.next(false);
                        observer.complete();
                    },
                });
            });

            return this.ongoingFetch$;
        }
        return false;
    }

    /**
     * Handle reapply page redirection based on user status and activity level.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * @returns {boolean} True if the user can access the reapply page, false otherwise.
     */
    private shouldAllowReapplyAccess(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {

        const { driverStatus, activityLevel, tabletId } = this.getUserInfo(userSummary);

        // Check if the route is for reapply page
        if (!route.routeConfig?.path?.includes(RoutePaths.REAPPLY)) {
            return false;
        }

        if (
            (
                driverStatus === DriverStatusEnum.FORMER_DRIVER &&
                [
                    ActivityLevelsEnum.GOOD_STANDING,
                    ActivityLevelsEnum.DELINQUENT_10,
                    ActivityLevelsEnum.DELINQUENT_20,
                    ActivityLevelsEnum.DELINQUENT_40,
                    ActivityLevelsEnum.DELINQUENT_60,
                    ActivityLevelsEnum.DELINQUENT_75,
                    ActivityLevelsEnum.TIA,
                    ActivityLevelsEnum.INVOICED_RETURNED,
                    ActivityLevelsEnum.COVID,
                ].includes(activityLevel as ActivityLevelsEnum)
            ) ||
            (
                driverStatus === DriverStatusEnum.FORMER_DRIVER_MAIL_DELIVERED &&
                ![
                    ActivityLevelsEnum.INVOICED,
                    ActivityLevelsEnum.MIA_NOT_CHARGED,
                    ActivityLevelsEnum.SWAP,
                    ActivityLevelsEnum.REAPPLY,
                    ActivityLevelsEnum.CHARGE_SUCCESSFUL,
                    ActivityLevelsEnum.CHARGE_REFUNDED,
                    ActivityLevelsEnum.CHARGE_FAILED,
                    ActivityLevelsEnum.INVOICED_TRANSIT,
                    ActivityLevelsEnum.LYFT_GOOD_STANDING,
                ].includes(activityLevel as ActivityLevelsEnum) &&
                (
                    !tabletId ||
                    tabletId === 'null' ||
                    tabletId === 'undefined'
                )
            )
        ) {
            return true;
        }
        return false;
    }

    /**
     * Check if the driver is an Uber Partner referral and has connected Uber account or has matched Journey TV identifier.
     * Users referred by Uber Partners must meet one of the two requirements to proceed:
     * 1. Journey TV Match: Must have a matched identifier to verify their Uber driving history
     * 2. Uber Connect Status: Must have successfully connected their Uber account (status = 'CONNECTED')
     * If either requirement is not met, the user is logged out and denied access to prevent
     * unauthorized or incomplete sign-ups from Uber Partner referrals.
     * 
     * @param {UserSummary} userSummary User Summary object.
     * @returns {boolean} True if the driver is an invalid Uber Partner referral, false otherwise.
     * 
     */
    private isInvalidUberPartnerReferral(userSummary: UserSummary): boolean {
        const isUberPartner = userSummary?.referral_info?.referred_by?.startsWith('Uber_Partner');
        if (!isUberPartner) return false;

        const hasJourneyTvMatch = (userSummary?.journey_tv?.matched_identifier?.length ?? 0) > 0;
        const isUberConnected = userSummary?.uber_connect?.status?.toUpperCase() === 'CONNECTED';

        return !hasJourneyTvMatch && !isUberConnected;
    }

    /**
     * Handle Invalid Uber Partner Referral redirection.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {boolean} True if the driver is an invalid Uber Partner referral, false otherwise.
     */
    private handleInvalidUberPartnerReferralRedirection(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {

        const currentPath = route.routeConfig?.path;

        // Do not check Uber Partner referral if accessing uber-link page
        if (currentPath === RoutePaths.UBER_LINK) {
            return true;
        }

        if (this.isInvalidUberPartnerReferral(userSummary)) {
            this.router.navigate([RoutePaths.UBER_AUTHORIZE], {
                queryParams: { error: 'uber_partner_referral_not_connected' }
            });
            return true;
        }
        return false;

    }

    /**
     * Handle mail in process redirection.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {boolean} True if the user can access the mail in process page, false otherwise.
     */
    private canAccessPaymentWhileMailInProcess(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {
        const { driverStatus } = this.getUserInfo(userSummary);

        // Mail in process can access payment page
        if (
            driverStatus === DriverStatusEnum.MAIL_IN_PROCESS &&
            route.routeConfig?.path === RoutePaths.PAYMENT
        ) {
            return true;
        }
        return false;
    }

    /**
     * Check if the driver can access funnel pages (proof_of_rides, payment, profile, info). The proof_of_rides page access is controlled
     * by the ENFORCE_UBER_CONNECT_FLAG environment variable.
     * The intent is to allow progressive access (TOS_COMPLETE can access info, DRIVER_INFO_COMPLETE can access info+profile, etc.).
     * Reapply activity level cannot access funnel pages.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {boolean} True if the driver can access funnel pages, false otherwise.
     */
    private canAccessFunnelPages(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {

        const { driverStatus, activityLevel } = this.getUserInfo(userSummary);
        const currentPath = route.routeConfig?.path;

        // Determine allowed paths based on driver status
        const allowedPaths: { [key: string]: string[] } = {
            [DriverStatusEnum.TOS_COMPLETE]: [RoutePaths.INFO],
            [DriverStatusEnum.DRIVER_INFO_COMPLETE]: [RoutePaths.INFO, RoutePaths.PROFILE],
            [DriverStatusEnum.PROFILE_COMPLETE]: [RoutePaths.INFO, RoutePaths.PROFILE, RoutePaths.PAYMENT],
            [DriverStatusEnum.PAYMENT_COMPLETE]: (
                environment.ENFORCE_UBER_CONNECT_FLAG
                    ? [RoutePaths.INFO, RoutePaths.PROFILE, RoutePaths.PAYMENT]
                    : [RoutePaths.INFO, RoutePaths.PROFILE, RoutePaths.PAYMENT, RoutePaths.PROOF_OF_RIDES]
            ),
        };
        const allowedFunnelPaths = allowedPaths[driverStatus] || [];

        if (!currentPath || !allowedFunnelPaths.includes(currentPath) || activityLevel === ActivityLevelsEnum.REAPPLY) {
            return false;
        }
        return true;
    }

    /**
     * Check if the driver can access the complete page based on their status, texted logs value, and total rides.
     * Payment complete and texted_logs "yes" with rides > 0 or "uber_pass" or "uber_fail" prefix can access complete page.
     * Reapply activity level cannot access complete page if allowUberConnection is true.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {boolean} True if the driver can access the complete page, false otherwise.
     */
    private canAccessCompletePage(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {
        const { driverStatus, textedLogs, lifetimeRides, activityLevel, allowUberConnection } = this.getUserInfo(userSummary);

        // Must be on the complete page
        if (route.routeConfig?.path !== RoutePaths.COMPLETE) {
            return false;
        }

        // Must have payment complete status
        if (driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE) {
            return false;
        }

        // If activity level is reapply, check if uber connection is allowed
        if (
            activityLevel === ActivityLevelsEnum.REAPPLY &&
            allowUberConnection
        ) {
            return false;
        }


        // Check texted logs and rides conditions
        const hasCompletedRides = textedLogs === TextedLogsEnum.YES && lifetimeRides > 0;
        const hasUberLogs = textedLogs.startsWith('uber_pass') || textedLogs.startsWith('uber_fail');
        const isReapplyWithUberConnection = activityLevel === ActivityLevelsEnum.REAPPLY && !allowUberConnection;
        const isUberConnectedButLogsNotUpdated = !allowUberConnection && (textedLogs === TextedLogsEnum.NO || !textedLogs);

        return hasCompletedRides || hasUberLogs || isReapplyWithUberConnection || isUberConnectedButLogsNotUpdated;
    }

    /**
     * Check if the driver can access the incomplete-and-contact-support page based on their status, texted logs value, and allow_uber_connection flag.
     * Payment complete, allow_uber_connection false, and texted_logs not "yes" or starting with "uber_" can access incomplete-and-contact-support page
     *
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     *
     * @returns {boolean} True if the driver can access the incomplete-and-contact-support page, false otherwise.
     */
    private canAccessIncompleteAndContactSupportPage(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {
        const { driverStatus, textedLogs, allowUberConnection } = this.getUserInfo(userSummary);

        // Must be on the incomplete-and-contact-support page
        if (route.routeConfig?.path !== RoutePaths.INCOMPLETE_AND_CONTACT_SUPPORT) {
            return false;
        }

        // Must have payment complete status
        if (driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE) {
            return false;
        }

        // Driver who has not yet connected Uber should not access this page
        if (allowUberConnection) {
            return false;
        }

        // Driver who has successfully connected Uber should not access this page
        if (
            !allowUberConnection &&
            textedLogs === TextedLogsEnum.NO
        ) {
            return false;
        }

        // Texted logs must not be "yes" or start with "uber_" and allow_uber_connection must be false
        return (
            textedLogs !== TextedLogsEnum.YES &&
            !(textedLogs?.startsWith('uber_'))
        );
    }

    /**
     * Check if the driver can access the uber-connect-required page based on their status, texted logs value, and allow_uber_connection flag.
     * Payment complete, allow_uber_connection true, or texted_logs "uber_required_manual_logs_failed" can access uber-connect-required page
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {boolean} True if the driver can access the uber-connect-required page, false otherwise.
     */
    private canAccessUberConnectRequiredPage(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {
        const { driverStatus, textedLogs, allowUberConnection } = this.getUserInfo(userSummary);

        // Must be on the uber-connect-required page
        if (route.routeConfig?.path !== RoutePaths.UBER_CONNECT_REQUIRED) {
            return false;
        }

        // Must have payment complete status
        if (driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE) {
            return false;
        }

        // Must be allowed to connect Uber or have failed manual logs
        return allowUberConnection || textedLogs === TextedLogsEnum.UBER_REQUIRED_MANUAL_LOGS_FAILED;
    }

    /**
     * Handle uber already connected redirection.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {boolean} True if the driver is redirected, false otherwise.
     */
    private handleInvalidUberConnectAccess(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {
        const { textedLogs, allowUberConnection, activityLevel } = this.getUserInfo(userSummary);

        // Must be on the uber-connect-required page
        if (route.routeConfig?.path !== RoutePaths.UBER_CONNECT_REQUIRED) {
            return false;
        }

        // Must not be allowed to connect Uber
        if (!allowUberConnection) {
            if (
                textedLogs === TextedLogsEnum.UBER_PASS ||
                textedLogs === TextedLogsEnum.YES ||
                activityLevel === ActivityLevelsEnum.REAPPLY ||
                textedLogs === TextedLogsEnum.NO
            ) {
                this.router.navigate([RoutePaths.COMPLETE]);
                return true;
            }
            if (
                textedLogs?.startsWith('uber_pass') ||
                textedLogs?.startsWith('uber_fail')
            ) {
                this.router.navigate([RoutePaths.COMPLETE]);
                return true;
            }

            // Neither uber_pass nor starts with uber_
            this.router.navigate([RoutePaths.INCOMPLETE_AND_CONTACT_SUPPORT]);
            return true;
        }
        return false;
    }

    /**
     * Redirect the driver to the correct funnel page based on their status, texted logs value, and allow_uber_connection flag.
     * 
     * @param {UserSummary} userSummary User Summary object.
     */
    private redirectToCorrectFunnelPage(
        userSummary: UserSummary
    ): void {

        const { driverStatus, textedLogs, allowUberConnection } = this.getUserInfo(userSummary);

        // Redirect to the status page if not payment complete
        if (driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE) {
            this.router.navigate([statusRoute[driverStatus]]);
            return;
        }

        // Redirect to uber-connect-required page if allowed to connect Uber
        if (allowUberConnection) {
            this.router.navigate([RoutePaths.UBER_CONNECT_REQUIRED]);
            return;
        }

        if (
            !allowUberConnection &&
            (textedLogs?.startsWith('uber_pass') || textedLogs?.startsWith('uber_fail'))
        ) {
            this.router.navigate([RoutePaths.COMPLETE]);
            return;
        }

        this.router.navigate([RoutePaths.INCOMPLETE_AND_CONTACT_SUPPORT]);
        return;
    }


    /**
     * Check if the proof of rides route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the proof of rides route is restricted, false otherwise.
     */
    private isProofOfRidesRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.PROOF_OF_RIDES &&
            driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE
        );
    }

    /**
     * Check if the complete route is restricted. If not payment complete, cannot access complete page.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the complete route is restricted, false otherwise.
     */
    private isCompleteRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {

        return (
            browserRoute.routeConfig?.path === RoutePaths.COMPLETE &&
            driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE
        )
    }

    /**
     * Check if the driver should be redirected to the complete page based on their status, texted logs value, and total rides.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * @param {string} driverTextedLogsValue Texted logs value.
     * @param {number} totalRides Total rides completed by the driver.
     * @param {boolean} isAllowedToConnectUber Whether the driver is allowed to connect Uber.
     * 
     * @returns {boolean} True if the driver should be redirected to the complete page, false otherwise.
     */
    private shouldRedirectToCompletePage(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
        driverTextedLogsValue: string,
        totalRides: number,
        isAllowedToConnectUber: boolean,
    ): boolean {
        return (
            browserRoute.routeConfig?.path !== RoutePaths.COMPLETE &&
            driverStatus === DriverStatusEnum.PAYMENT_COMPLETE &&
            ((
                (driverTextedLogsValue === TextedLogsEnum.YES && totalRides > 0) ||
                (driverTextedLogsValue === TextedLogsEnum.UBER_PASS)
            ) || 
            (
                driverTextedLogsValue === TextedLogsEnum.NO &&
                !isAllowedToConnectUber
            ))
        );
    }

    /**
     * Check if the payment route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the payment route is restricted, false otherwise.
     */
    private isPaymentRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.PAYMENT &&
            driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE &&
            driverStatus !== DriverStatusEnum.PROFILE_COMPLETE
        );
    }

    /**
     * Check if the profile route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the profile route is restricted, false otherwise.
     */
    private isProfileRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.PROFILE &&
            driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE &&
            driverStatus !== DriverStatusEnum.PROFILE_COMPLETE &&
            driverStatus !== DriverStatusEnum.DRIVER_INFO_COMPLETE
        );
    }

    /**
     * Check if the info route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the info route is restricted, false otherwise.
     */
    private isInfoRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.INFO &&
            driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE &&
            driverStatus !== DriverStatusEnum.PROFILE_COMPLETE &&
            driverStatus !== DriverStatusEnum.DRIVER_INFO_COMPLETE &&
            driverStatus !== DriverStatusEnum.TOS_COMPLETE
        );
    }

    /**
     * Check if the Uber Connect Required route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the Uber Connect Required route is restricted, false otherwise.
     */
    private isUberConnectRequiredRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.UBER_CONNECT_REQUIRED &&
            driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE
        );
    }

    /**
     * Check if the incomplete and contact support route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the incomplete and contact support route is restricted, false otherwise.
     */
    private isIncompleteAndContactSupportRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.INCOMPLETE_AND_CONTACT_SUPPORT &&
            driverStatus !== DriverStatusEnum.PAYMENT_COMPLETE
        );
    }

    /**
     * Check if the do not contact route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the do not contact route is restricted, false otherwise.
     */
    private isDoNotContactRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.DO_NOT_CONTACT &&
            driverStatus !== DriverStatusEnum.DO_NOT_CONTACT
        ) || (
                browserRoute.routeConfig?.path !== RoutePaths.DO_NOT_CONTACT &&
                driverStatus === DriverStatusEnum.DO_NOT_CONTACT
            );
    }

    /**
     * Check if the ready for tablet route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the ready for tablet route is restricted, false otherwise.
     */
    private isReadyForTabletRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.READY_FOR_TABLET &&
            driverStatus !== DriverStatusEnum.READY_FOR_TABLET
        ) || (
                browserRoute.routeConfig?.path !== RoutePaths.READY_FOR_TABLET &&
                driverStatus === DriverStatusEnum.READY_FOR_TABLET
            );
    }

    /**
     * Check if the mail in process route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * 
     * @returns {boolean} True if the mail in process route is restricted, false otherwise.
     */
    private isMailInProcessRouteRestricted(
        driverStatus: string,
        route: ActivatedRouteSnapshot,
    ): boolean {
        return (
            route.routeConfig?.path === RoutePaths.MAIL_IN_PROCESS &&
            driverStatus !== DriverStatusEnum.MAIL_IN_PROCESS
        ) || (
                route.routeConfig?.path !== RoutePaths.MAIL_IN_PROCESS &&
                driverStatus === DriverStatusEnum.MAIL_IN_PROCESS
            );
    }

    /**
     * Check if the mailing issue route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the mailing issue route is restricted, false otherwise.
     */
    private isMailingIssueRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.MAILING_ISSUE &&
            driverStatus !== DriverStatusEnum.MAILING_ISSUE
        ) || (
                browserRoute.routeConfig?.path !== RoutePaths.MAILING_ISSUE &&
                driverStatus === DriverStatusEnum.MAILING_ISSUE
            );
    }
    /**
     * Check if the not interested route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the not interested route is restricted, false otherwise.
     */
    private isNotInterestedRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.NOT_INTERESTED &&
            driverStatus !== DriverStatusEnum.NOT_INTERESTED
        ) || (
                browserRoute.routeConfig?.path !== RoutePaths.NOT_INTERESTED &&
                driverStatus === DriverStatusEnum.NOT_INTERESTED
            );
    }

    /**
     * Check if the waitlist route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the waitlist route is restricted, false otherwise.
     */
    private isWaitlistRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.WAITLIST &&
            driverStatus !== DriverStatusEnum.INTERESTED_DNQ_RIDES
        ) || (
                browserRoute.routeConfig?.path !== RoutePaths.WAITLIST &&
                driverStatus === DriverStatusEnum.INTERESTED_DNQ_RIDES
            );
    }

    /**
     * Check if the waitlist other route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the waitlist other route is restricted, false otherwise.
     */
    private isWaitlistOtherRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.WAITLIST_OTHER &&
            (driverStatus !== DriverStatusEnum.INTERESTED_DNQ_MARKET &&
                driverStatus !== DriverStatusEnum.INTERESTED_DNQ_OTHER)
        ) || (
                browserRoute.routeConfig?.path !== RoutePaths.WAITLIST_OTHER &&
                (driverStatus === DriverStatusEnum.INTERESTED_DNQ_MARKET ||
                    driverStatus === DriverStatusEnum.INTERESTED_DNQ_OTHER)
            );
    }

    /**
     * Check if the waitlist demand route is restricted.
     * 
     * @param {string} driverStatus Driver status.
     * @param {ActivatedRouteSnapshot} browserRoute Route Snapshot.
     * 
     * @returns {boolean} True if the waitlist demand route is restricted, false otherwise.
     */
    private isWaitlistDemandRouteRestricted(
        driverStatus: string,
        browserRoute: ActivatedRouteSnapshot,
    ): boolean {
        return (
            browserRoute.routeConfig?.path === RoutePaths.WAITLIST_DEMAND &&
            driverStatus !== DriverStatusEnum.WAITLISTED
        ) || (
                browserRoute.routeConfig?.path !== RoutePaths.WAITLIST_DEMAND &&
                driverStatus === DriverStatusEnum.WAITLISTED
            );
    }

    /**
     * Check if the driver can access Ready For Tablet or beyond pages based on their status and the current route.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {string} driverStatus Driver status.
     * 
     * @returns {boolean} True if the driver can access Ready For Tablet or beyond pages, false otherwise.
     */
    private canAccessRFTOrBeyond(
        route: ActivatedRouteSnapshot,
        driverStatus: string
    ): boolean {

        const currentPath = route.routeConfig?.path;

        if (
            !currentPath ||
            ![
                RoutePaths.READY_FOR_TABLET,
                RoutePaths.MAIL_IN_PROCESS,
                RoutePaths.MAILING_ISSUE,
            ].includes(currentPath as RoutePaths)
        ) {
            return false;
        }

        if (
            driverStatus === DriverStatusEnum.READY_FOR_TABLET &&
            currentPath === RoutePaths.READY_FOR_TABLET
        ) {
            return true;
        }

        if (
            driverStatus === DriverStatusEnum.MAIL_IN_PROCESS &&
            currentPath === RoutePaths.MAIL_IN_PROCESS
        ) {
            return true;
        }

        if (
            driverStatus === DriverStatusEnum.MAILING_ISSUE &&
            currentPath === RoutePaths.MAILING_ISSUE
        ) {
            return true;
        }

        return false;
    }

    /**
     * Check if the driver can access the Do Not Contact page based on their status and the current route.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {string} driverStatus Driver status.
     * 
     * @returns {boolean} True if the driver can access the Do Not Contact page, false otherwise.
     */
    private canAccessDoNotContactPage(
        route: ActivatedRouteSnapshot,
        driverStatus: string
    ): boolean {
        const currentPath = route.routeConfig?.path;

        if (currentPath !== RoutePaths.DO_NOT_CONTACT) {
            return false;
        }

        if (
            driverStatus === DriverStatusEnum.DO_NOT_CONTACT &&
            currentPath === RoutePaths.DO_NOT_CONTACT
        ) {
            return true;
        }

        return false;
    }

    /**
     * Check if the driver can access the Not Interested page based on their status and the current route.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {string} driverStatus Driver status.
     * 
     * @returns {boolean} True if the driver can access the Not Interested page, false otherwise.
     */
    private canAccessNotInterestedPage(
        route: ActivatedRouteSnapshot,
        driverStatus: string
    ): boolean {
        const currentPath = route.routeConfig?.path;

        if (currentPath !== RoutePaths.NOT_INTERESTED) {
            return false;
        }

        if (
            driverStatus === DriverStatusEnum.NOT_INTERESTED &&
            currentPath === RoutePaths.NOT_INTERESTED
        ) {
            return true;
        }

        return false;
    }

    private canAccessWaitlistPages(
        route: ActivatedRouteSnapshot,
        driverStatus: string
    ): boolean {
        const currentPath = route.routeConfig?.path;

        if (
            !currentPath ||
            ![
                RoutePaths.WAITLIST,
                RoutePaths.WAITLIST_OTHER,
                RoutePaths.WAITLIST_DEMAND,
            ].includes(currentPath as RoutePaths)
        ) {
            return false;
        }

        if (
            driverStatus === DriverStatusEnum.INTERESTED_DNQ_RIDES &&
            currentPath === RoutePaths.WAITLIST
        ) {
            return true;
        }

        if (
            (driverStatus === DriverStatusEnum.INTERESTED_DNQ_MARKET ||
                driverStatus === DriverStatusEnum.INTERESTED_DNQ_OTHER) &&
            currentPath === RoutePaths.WAITLIST_OTHER
        ) {
            return true;
        }

        if (
            driverStatus === DriverStatusEnum.WAITLISTED &&
            currentPath === RoutePaths.WAITLIST_DEMAND
        ) {
            return true;
        }

        return false;
    }

    /**
     * Check if the driver can access the Uber Link page based on their status and the current route.
     * 
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * 
     * @returns {boolean} True if the driver can access the Uber Link page, false otherwise.
     */
    private canAccessUberLinkPage(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {
        const { driverStatus } = this.getUserInfo(userSummary);
        const currentPath = route.routeConfig?.path;

        // Check if the route is for uber link page
        if (currentPath !== RoutePaths.UBER_LINK) {
            return false;
        }

        if (
            [
                DriverStatusEnum.ACTIVE_DRIVER,
                DriverStatusEnum.FORMER_DRIVER,
                DriverStatusEnum.FORMER_DRIVER_MAIL_DELIVERED,
                DriverStatusEnum.MAIL_DELIVERED,
            ].includes(driverStatus as DriverStatusEnum)
        ) {
            return true;
        }
        return false;
    }

    /**
     * Pipeline drivers whose Uber authorization was revoked are redirected to the relink page;
     * UBER_REAUTH_DASHBOARD_STATUSES get a dashboard panel instead and UBER_REAUTH_EXCLUDED_STATUSES
     * get no reauth flow at all.
     * The flag only exists on driver/summary/,
     * not the driver/join/ payload this guard uses, so it's fetched separately and cached per auth token
     * (logout doesn't reload the page, so a plain cache would leak across users).
     */
    private isReauthRedirectCandidate(route: ActivatedRouteSnapshot, userSummary: UserSummary): boolean {
        const { driverStatus } = this.getUserInfo(userSummary);
        return (
            !UBER_REAUTH_DASHBOARD_STATUSES.includes(driverStatus) &&
            !UBER_REAUTH_EXCLUDED_STATUSES.includes(driverStatus) &&
            route.routeConfig?.path !== RoutePaths.UBER_LINK
        );
    }

    private getCachedReauthStatus(): boolean | null {
        const token = this.authService.getToken();
        return this.reauthCache && this.reauthCache.token === token ? this.reauthCache.needsReauth : null;
    }

    // Errors resolve to (and cache) false, so a failing /summary never blocks or slows existing navigation.
    private fetchReauthStatus(): Observable<boolean> {
        const token = this.authService.getToken();
        return this.apiDriverInterfaceService.get('driver/summary/', null).pipe(
            map((response: any) => !!response?.data?.is_uber_reauth_needed),
            catchError(() => of(false)),
            tap((needsReauth) => (this.reauthCache = { token, needsReauth }))
        );
    }

    // Stays synchronous (identical to statusupdate()) whenever the flag is already known. If it isn't known
    // yet, waits for it before running statusupdate(), so the funnel's own redirects (e.g. to /complete)
    // don't fire before the relink redirect.
    private statusUpdateWithReauthCheck(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean | Observable<boolean> {
        if (!this.isReauthRedirectCandidate(route, userSummary)) {
            return this.statusupdate(route, userSummary);
        }
        const cached = this.getCachedReauthStatus();
        if (cached === true) {
            this.router.navigate([RoutePaths.UBER_LINK]);
            return false;
        }
        if (cached === false) {
            return this.statusupdate(route, userSummary);
        }
        return this.resolveWithReauthCheck(route, userSummary);
    }

    // Async path (already waiting on driver/join/): waits for the flag too, so there's no flash of the funnel page.
    private resolveWithReauthCheck(route: ActivatedRouteSnapshot, userSummary: UserSummary): Observable<boolean> {
        if (!this.isReauthRedirectCandidate(route, userSummary)) {
            return of(this.statusupdate(route, userSummary));
        }
        const cached = this.getCachedReauthStatus();
        const needsReauth$ = cached === null ? this.fetchReauthStatus() : of(cached);
        return needsReauth$.pipe(
            map((needsReauth) => {
                if (needsReauth) {
                    this.router.navigate([RoutePaths.UBER_LINK]);
                    return false;
                }
                return this.statusupdate(route, userSummary);
            })
        );
    }

    /**
     * Update status and redirect based on status and route.
     * @param {ActivatedRouteSnapshot} route Route Snapshot.
     * @param {UserSummary} userSummary User Summary object.
     * @returns {boolean} True if the user can access the route, false otherwise.
     */
    private statusupdate(
        route: ActivatedRouteSnapshot,
        userSummary: UserSummary
    ): boolean {

        const { driverStatus, textedLogs, lifetimeRides, allowUberConnection } = this.getUserInfo(userSummary);

        // Reapply page redirection
        if (this.shouldAllowReapplyAccess(route, userSummary)) return true;

        // Uber Partner Referral Validation
        if (this.handleInvalidUberPartnerReferralRedirection(route, userSummary)) return true;

        // Mail in process can access payment page
        if (this.canAccessPaymentWhileMailInProcess(route, userSummary)) return true;

        // Funnel pages access (proof_of_rides, payment, profile, info) check
        if (this.canAccessFunnelPages(route, userSummary)) return true;

        // Payment complete and texted_logs "yes" with rides > 0 or "uber_pass" or "uber_fail" prefix can access complete page
        if (this.canAccessCompletePage(route, userSummary)) return true;

        // RFT and beyond pages access check
        if (this.canAccessRFTOrBeyond(route, driverStatus)) return true;

        // Do not contact page access check
        if (this.canAccessDoNotContactPage(route, driverStatus)) return true;

        // Not intetested page access check
        if (this.canAccessNotInterestedPage(route, driverStatus)) return true;

        // Waitlist and related pages access check
        if (this.canAccessWaitlistPages(route, driverStatus)) return true;

        // Payment complete and texted_logs "uber_required_manual_logs_failed" can access uber-connect-required page
        // Payment complete and allowed uber connection true can access uber-connect-required page
        if (this.canAccessUberConnectRequiredPage(route, userSummary)) {
            return true;
        } else if (this.handleInvalidUberConnectAccess(route, userSummary)) {
            return false;
        }

        // Payment complete and texted_logs not "yes" or starting with "uber_" and allow_uber_connection false can access incomplete-and-contact-support page
        if (this.canAccessIncompleteAndContactSupportPage(route, userSummary)) return true;

        // Should redirect to complete page
        if (this.shouldRedirectToCompletePage(driverStatus, route, textedLogs, lifetimeRides, allowUberConnection)) {
            this.router.navigate([RoutePaths.COMPLETE]);
            return false;
        }

        // Access uber link page
        if (this.canAccessUberLinkPage(route, userSummary)) return true;

        // General restrictions for various routes
        if (
            route.routeConfig?.path?.includes(RoutePaths.JOIN) ||
            route.routeConfig?.path?.includes(RoutePaths.LOGIN) ||
            route.routeConfig?.path?.includes(RoutePaths.RESET) ||
            route.routeConfig?.path?.includes(RoutePaths.SET_PASSWORD) ||
            this.isProofOfRidesRouteRestricted(driverStatus, route) ||
            this.isCompleteRouteRestricted(driverStatus, route) ||
            this.isPaymentRouteRestricted(driverStatus, route) ||
            this.isProfileRouteRestricted(driverStatus, route) ||
            this.isInfoRouteRestricted(driverStatus, route) ||
            this.isUberConnectRequiredRouteRestricted(driverStatus, route) ||
            this.isIncompleteAndContactSupportRouteRestricted(driverStatus, route) ||
            this.isDoNotContactRouteRestricted(driverStatus, route) ||
            this.isReadyForTabletRouteRestricted(driverStatus, route) ||
            this.isMailInProcessRouteRestricted(driverStatus, route) ||
            this.isMailingIssueRouteRestricted(driverStatus, route) ||
            this.isNotInterestedRouteRestricted(driverStatus, route) ||
            this.isWaitlistOtherRouteRestricted(driverStatus, route) ||
            this.isWaitlistRouteRestricted(driverStatus, route) ||
            this.isWaitlistDemandRouteRestricted(driverStatus, route)
        ) {
            this.router.navigate([statusRoute[driverStatus]]);
            return false;
        }

        // Fallback redirection
        this.redirectToCorrectFunnelPage(userSummary);
        return false;
    }
}
